import { ArrowDown, ArrowUp, ArrowUpDown, Columns } from 'lucide-react'
import type { ReactNode } from 'react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { TypeSortDirection } from '@/libs/interfaces'
import { cn } from '@/libs/utils'

import { isExcludedColumnKey } from './columnConventions'
import type { SortOption } from './ListFilters'

interface Column<T> {
  key: keyof T | string
  header: string
  render?: (item: T) => ReactNode
  className?: string
  sortable?: boolean
  searchable?: boolean
  alwaysVisible?: boolean
  hiddenByDefault?: boolean
}

interface DataTableProps<T> {
  columns: Column<T>[]
  data: T[]
  keyExtractor: (item: T) => string
  onRowClick?: (item: T) => void
  emptyMessage?: string
  externalSearchQuery?: string
  enableColumnToggle?: boolean
  hiddenColumns?: Record<string, boolean>
  sortOptions?: SortOption[]
  currentSort?: string
  sortDirection?: TypeSortDirection
  onSortChange?: (key: string, direction: TypeSortDirection) => void
}

export function DataTable<T>({
  columns,
  data,
  keyExtractor,
  onRowClick,
  emptyMessage = 'No data available',
  externalSearchQuery = '',
  enableColumnToggle = true,
  hiddenColumns: controlledHiddenColumns,
  sortOptions,
  currentSort,
  sortDirection,
  onSortChange,
}: DataTableProps<T>) {
  const { t } = useTranslation()
  const nonIdentifierColumns = useMemo(
    () => columns.filter((c) => !isExcludedColumnKey(String(c.key))),
    [columns],
  )
  const [internalHiddenColumns, setHiddenColumns] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {}
    nonIdentifierColumns.forEach((c) => {
      if (c.hiddenByDefault && !c.alwaysVisible) initial[String(c.key)] = true
    })
    return initial
  })
  const hiddenColumns = controlledHiddenColumns ?? internalHiddenColumns
  const isControlled = controlledHiddenColumns !== undefined

  const visibleColumns = useMemo(
    () => nonIdentifierColumns.filter((c) => c.alwaysVisible || !hiddenColumns[String(c.key)]),
    [nonIdentifierColumns, hiddenColumns],
  )

  const toggleableColumns = nonIdentifierColumns.filter((c) => !c.alwaysVisible)

  const isSortable = (column: Column<T>) =>
    column.sortable !== false && !!sortOptions?.some((option) => option.key === String(column.key))

  const handleSort = (columnKey: string) => {
    if (!onSortChange) return
    if (columnKey === currentSort) {
      onSortChange(columnKey, sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      onSortChange(columnKey, 'asc')
    }
  }

  const filteredData = useMemo(() => {
    let result = [...data]

    // Search filter using external query from ListFilters
    if (externalSearchQuery.trim()) {
      const query = externalSearchQuery.toLowerCase()
      result = result.filter((item) => {
        return columns.some((col) => {
          if (col.searchable === false) return false
          const value = item[col.key as keyof T]
          if (value === null || value === undefined) return false
          return String(value).toLowerCase().includes(query)
        })
      })
    }

    return result
  }, [data, columns, externalSearchQuery])

  const getSortIcon = (columnKey: string) => {
    if (currentSort !== columnKey) {
      return <ArrowUpDown className="w-3.5 h-3.5 text-muted-foreground/50" />
    }
    if (sortDirection === 'asc') {
      return <ArrowUp className="w-3.5 h-3.5 text-primary" />
    }
    return <ArrowDown className="w-3.5 h-3.5 text-primary" />
  }

  return (
    <div className="space-y-2 animate-slide-up">
      {enableColumnToggle && !isControlled && toggleableColumns.length > 0 && (
        <div className="flex justify-end">
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Columns className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">{t('filters.columns', 'Columns')}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-popover">
                  <DropdownMenuLabel>
                    {t('filters.toggleColumns', 'Toggle columns')}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {toggleableColumns.map((col) => {
                    const key = String(col.key)
                    return (
                      <DropdownMenuCheckboxItem
                        key={key}
                        checked={!hiddenColumns[key]}
                        onCheckedChange={() =>
                          setHiddenColumns((prev) => ({
                            ...prev,
                            [key]: !prev[key],
                          }))
                        }
                        onSelect={(e) => e.preventDefault()}
                      >
                        {col.header}
                      </DropdownMenuCheckboxItem>
                    )
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </TooltipTrigger>
            <TooltipContent>{t('tooltips.filters.columns', 'Toggle columns')}</TooltipContent>
          </Tooltip>
        </div>
      )}
      <div className="surface-card overflow-hidden">
        <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="table-head-row">
                {visibleColumns.map((column) => {
                  const sortable = isSortable(column)
                  return (
                    <th
                      key={String(column.key)}
                      className={cn(
                        'table-head-cell',
                        sortable && 'table-head-cell-sortable',
                        column.className,
                      )}
                      onClick={() => sortable && handleSort(String(column.key))}
                    >
                      <div className="flex items-center gap-1.5">
                        <span>{column.header}</span>
                        {sortable && getSortIcon(String(column.key))}
                      </div>
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {filteredData.length === 0 ? (
                <tr>
                  <td
                    colSpan={visibleColumns.length}
                    className="py-12 text-center text-muted-foreground"
                  >
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                filteredData.map((item, index) => (
                  <tr
                    key={keyExtractor(item)}
                    className={cn('table-body-row', onRowClick && 'cursor-pointer')}
                    onClick={() => onRowClick?.(item)}
                    style={{ animationDelay: `${index * 30}ms` }}
                  >
                    {visibleColumns.map((column) => (
                      <td
                        key={String(column.key)}
                        className={cn('table-body-cell', column.className)}
                      >
                        {column.render
                          ? column.render(item)
                          : String(item[column.key as keyof T] ?? '')}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
